import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import ChartCard from './ChartCard';
import ChartLegend from './ChartLegend';
import { RechartsTooltip } from './ChartTooltip';
import {
  BAR_MAX_SIZE,
  CHART_INK,
  CHART_SURFACE,
  STATUS_COLORS,
  WEATHER_COLORS,
  Y_AXIS_WIDTH,
} from './chartTheme';
import WeatherStrips, { formatRain, formatTemperature } from './WeatherStrips';
import { formatBucketLabel, formatBucketTitle } from './formatters';
import { getWeekendLegendItem, renderWeekendLines } from './weekendLines';
import { useStatusLabels } from './useStatusLabels';
import {
  BucketWeather,
  emptyStatusCounts,
  STATUS_KEYS,
  StatusCounts,
  TimeBucket,
} from '../../utils/statistics';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';

interface ReservationsChartProps {
  buckets: TimeBucket[];
  /** Weather per bucket key; the weather strips are hidden without it. */
  weather?: Map<string, BucketWeather>;
}

let chartIdCounter = 0;

const ReservationsChart = ({ buckets, weather }: ReservationsChartProps) => {
  const { t, i18n } = useTranslation();

  const labels = useStatusLabels();

  const totals = useMemo(
    () =>
      buckets.reduce<StatusCounts>((acc, bucket) => {
        STATUS_KEYS.forEach((key) => {
          acc[key] += bucket[key];
        });

        return acc;
      }, emptyStatusCounts()),
    [buckets]
  );

  const [syncId] = useState(() => `reservations-${(chartIdCounter += 1)}`);

  const data = useMemo(
    () =>
      buckets.map((b) => ({
        ...b,
        label: formatBucketLabel(dayjs(b.date), b.unit, buckets.length, i18n.language),
        title: formatBucketTitle(dayjs(b.date), b.unit, i18n.language),
        temperature: weather?.get(b.key)?.temperature ?? null,
        rain: weather?.get(b.key)?.rain ?? null,
      })),
    [buckets, weather, i18n.language]
  );

  const hasWeather = data.some(
    (point) => point.temperature !== null || point.rain !== null
  );

  const getWeatherRows = (point: (typeof data)[number]) =>
    hasWeather
      ? [
          {
            key: 'temperature',
            label: t(COMMON_TRANSLATE_KEYS.STATS_TEMPERATURE),
            color: WEATHER_COLORS.temperature,
            value:
              point.temperature === null
                ? '–'
                : formatTemperature(point.temperature),
          },
          {
            key: 'rain',
            label: t(COMMON_TRANSLATE_KEYS.STATS_RAIN),
            color: WEATHER_COLORS.rain,
            value: point.rain === null ? '–' : formatRain(point.rain),
          },
        ]
      : [];

  return (
    <ChartCard
      title={t(COMMON_TRANSLATE_KEYS.STATS_RESERVATIONS_CHART)}
      subtitle={t(COMMON_TRANSLATE_KEYS.STATS_RESERVATIONS_CHART_HINT)}
      legend={
        <ChartLegend
          items={[
            ...STATUS_KEYS.map((key) => ({
              key,
              label: labels[key],
              color: STATUS_COLORS[key],
              value: String(totals[key]),
            })),
            ...getWeekendLegendItem(
              buckets,
              t(COMMON_TRANSLATE_KEYS.STATS_WEEKEND)
            ),
          ]}
        />
      }
    >
      <div className="h-[280px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            syncId={syncId}
          >
            <CartesianGrid vertical={false} stroke={CHART_INK.grid} />
            <XAxis
              dataKey="label"
              tick={{ fill: CHART_INK.muted, fontSize: 12 }}
              axisLine={{ stroke: CHART_INK.axis }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={8}
            />
            <YAxis
              width={Y_AXIS_WIDTH}
              allowDecimals={false}
              tick={{ fill: CHART_INK.muted, fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              cursor={{ fill: 'rgba(255,255,255,0.05)' }}
              content={
                <RechartsTooltip
                  labels={labels}
                  getTitle={(item) => item.title}
                  getExtraRows={getWeatherRows}
                />
              }
            />
            {STATUS_KEYS.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                stackId="status"
                fill={STATUS_COLORS[key]}
                stroke={CHART_SURFACE}
                strokeWidth={1}
                maxBarSize={BAR_MAX_SIZE}
                radius={index === STATUS_KEYS.length - 1 ? [4, 4, 0, 0] : 0}
                isAnimationActive={false}
              />
            ))}
            {renderWeekendLines(data)}
          </BarChart>
        </ResponsiveContainer>
      </div>
      {hasWeather && <WeatherStrips data={data} syncId={syncId} />}
    </ChartCard>
  );
};

export default ReservationsChart;
